import { type ApiClient, isApiError } from "@simpro/api-client";
import type { ZodType } from "zod";
import type { AnySession } from "../schemas/auth.schemas";
import { useAuthStore } from "../store/auth.store";

export interface SessionCoreOptions {
  apiClient: ApiClient;
  /** Full path of the refresh endpoint, for example `/api/v1/auth/refresh`. */
  refreshPath: string;
  /** Parses the refresh response. Staff and customer sessions differ only in their principal. */
  sessionSchema: ZodType<AnySession>;
  /** BroadcastChannel name shared by tabs of the same app. */
  channelName: string;
}

export interface SessionCore {
  applySession: (session: AnySession, options: { broadcast: boolean }) => void;
  clearSession: (options: { broadcast: boolean }) => void;
  /**
   * Exchanges the refresh cookie for a new session. Single flight per tab, leased across tabs.
   * Resolves null only when the server refused the refresh token (the session is over); rejects,
   * keeping the session and retrying with backoff, when the refresh could not be tried.
   */
  refreshSession: () => Promise<string | null>;
  /** The live session, restoring it from storage first if needed. */
  ensureSession: () => Promise<AnySession | null>;
  /**
   * Drops the session in this tab and tells the others. `signedOut` when a person chose to sign
   * out, rather than the session running out: login then forgets the page they were on.
   */
  signOutLocally: (options?: { signedOut?: boolean }) => void;
}

type SessionMessage =
  | { type: "session"; session: AnySession }
  // `signedOut` travels with it: a person signing out in one tab signed out in every tab.
  | { type: "logout"; signedOut?: boolean };

const LEASE_KEY = "simpro.refreshLease";
const LEASE_TTL_MS = 15_000;
const LEASE_WAIT_MS = 8_000;
/** Refresh when a quarter of the access token's lifetime is left. */
const REFRESH_AT_REMAINING = 0.25;
const MIN_REFRESH_DELAY_MS = 5_000;
/** Backoff for a refresh that could not be tried: 2 s, 4 s, 8 s ... up to a minute. */
const RETRY_BASE_MS = 2_000;
const RETRY_MAX_MS = 60_000;

/**
 * The answers that mean the refresh token is no good, so the session is over: any 401 (the staff,
 * platform and customer refresh routes all answer one), or one of their refusal codes whatever the
 * status. Everything else (offline, 429, 5xx, a 409 lost rotation race) leaves the session alone.
 */
const REFRESH_REFUSED_CODES: ReadonlySet<string> = new Set([
  "auth.invalid_refresh_token",
  "auth.refresh_token_reused",
  "identity.customer_invalid_refresh_token",
  // The workspace itself is closed: suspended (403) or offboarded and gone (404). Retrying cannot
  // help, and before 2026-10-13 it left staff on an endless "Loading" screen; signed out, they reach
  // the sign-in page, which says why.
  "tenant.suspended",
  "tenant.unknown",
]);

export function refreshRefused(error: unknown): boolean {
  return isApiError(error) && (error.statusCode === 401 || REFRESH_REFUSED_CODES.has(error.code));
}

/**
 * Everything about holding a session that does not depend on who the principal is: storing it,
 * refreshing it once across any number of concurrent callers and tabs, and telling the other tabs.
 *
 * Shared by the staff and customer factories rather than written twice. The two differ only in
 * which endpoint they call and what the principal looks like; rotation, the cross-tab lease and the
 * proactive refresh are subtle enough that a second copy would drift from this one and nobody would
 * notice until a token was reused.
 */
export function createSessionCore(options: SessionCoreOptions): SessionCore {
  const { apiClient, refreshPath, sessionSchema } = options;
  const store = useAuthStore;
  const tabId = Math.random().toString(36).slice(2);

  let refreshInFlight: Promise<string | null> | null = null;
  let refreshTimer: ReturnType<typeof setTimeout> | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let retryAttempt = 0;
  let sessionWaiters: ((session: AnySession | null) => void)[] = [];

  /* ---------- cross-tab channel ---------- */

  const channel =
    typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(options.channelName);

  channel?.addEventListener("message", (event: MessageEvent<SessionMessage>) => {
    const message = event.data;
    if (message.type === "session") {
      applySession(message.session, { broadcast: false });
    } else {
      clearSession({ broadcast: false, signedOut: message.signedOut ?? false });
    }
  });

  function broadcast(message: SessionMessage): void {
    channel?.postMessage(message);
  }

  /* ---------- session lifecycle ---------- */

  function applySession(
    session: AnySession,
    { broadcast: shouldBroadcast }: { broadcast: boolean },
  ): void {
    resetRetry();
    store.getState().setSession(session);
    scheduleProactiveRefresh(session);
    if (shouldBroadcast) broadcast({ type: "session", session });
    const waiters = sessionWaiters;
    sessionWaiters = [];
    for (const resolve of waiters) resolve(session);
  }

  function clearSession({
    broadcast: shouldBroadcast,
    signedOut = false,
  }: {
    broadcast: boolean;
    signedOut?: boolean;
  }): void {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = null;
    resetRetry();
    store.getState().clear({ signedOut });
    if (shouldBroadcast) broadcast({ type: "logout", signedOut });
    const waiters = sessionWaiters;
    sessionWaiters = [];
    for (const resolve of waiters) resolve(null);
  }

  function scheduleProactiveRefresh(session: AnySession): void {
    if (refreshTimer) clearTimeout(refreshTimer);
    const remaining = new Date(session.accessTokenExpiresAt).getTime() - Date.now();
    if (!Number.isFinite(remaining)) return;
    const delay = Math.max(remaining * (1 - REFRESH_AT_REMAINING), MIN_REFRESH_DELAY_MS);
    refreshTimer = setTimeout(() => {
      // A failure that is not the session ending schedules its own retry.
      refreshSession().catch(() => undefined);
    }, delay);
  }

  /* ---------- cross-tab refresh lease ---------- */

  function tryAcquireLease(): boolean {
    try {
      const now = Date.now();
      const existing = localStorage.getItem(LEASE_KEY);
      if (existing) {
        const stamp = Number(existing.slice(existing.indexOf(":") + 1));
        if (now - stamp < LEASE_TTL_MS) return false;
      }
      const entry = `${tabId}:${now}`;
      localStorage.setItem(LEASE_KEY, entry);
      return localStorage.getItem(LEASE_KEY) === entry;
    } catch {
      return true;
    }
  }

  function releaseLease(): void {
    try {
      if (localStorage.getItem(LEASE_KEY)?.startsWith(`${tabId}:`))
        localStorage.removeItem(LEASE_KEY);
    } catch {
      // ignore
    }
  }

  function waitForOtherTab(): Promise<AnySession | null> {
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        sessionWaiters = sessionWaiters.filter((w) => w !== onSession);
        resolve(null);
      }, LEASE_WAIT_MS);
      const onSession = (session: AnySession | null) => {
        clearTimeout(timer);
        resolve(session);
      };
      sessionWaiters.push(onSession);
    });
  }

  /**
   * Exchange the refresh cookie for a new session. Single flight within the
   * tab; across tabs the lease makes one tab do the work and the others wait for
   * its broadcast, so a rotated refresh token is never reused.
   */
  function refreshSession(): Promise<string | null> {
    refreshInFlight ??= (async () => {
      // The token is in an HttpOnly cookie this code cannot see (ADR-0005); the flag says whether
      // there is any point asking.
      if (!store.getState().hasSession) {
        clearSession({ broadcast: false });
        return null;
      }
      if (!tryAcquireLease()) {
        const fromOtherTab = await waitForOtherTab();
        if (fromOtherTab) return fromOtherTab.accessToken;
        if (!tryAcquireLease()) return store.getState().session?.accessToken ?? null;
      }
      try {
        const session = await apiClient.post(sessionSchema, refreshPath, undefined, {
          noRefresh: true,
        });
        applySession(session, { broadcast: true });
        return session.accessToken;
      } catch (error) {
        // Only the server saying the refresh token is no good ends the session. Offline, a 429
        // from a shared NAT, a 5xx or a lost rotation race (409) is not an answer about the
        // session: signing out every tab for one of those threw away a session that was fine.
        if (refreshRefused(error)) {
          clearSession({ broadcast: true });
          return null;
        }
        scheduleRetry();
        throw error;
      } finally {
        releaseLease();
      }
    })().finally(() => {
      refreshInFlight = null;
    });
    return refreshInFlight;
  }

  /** Tries again later, doubling the wait each time up to a ceiling, with jitter so tabs and users spread out. */
  function scheduleRetry(): void {
    if (retryTimer) clearTimeout(retryTimer);
    const base = Math.min(RETRY_BASE_MS * 2 ** retryAttempt, RETRY_MAX_MS);
    retryAttempt += 1;
    const delay = base / 2 + Math.random() * (base / 2);
    retryTimer = setTimeout(() => {
      retryTimer = null;
      refreshSession().catch(() => undefined);
    }, delay);
  }

  function resetRetry(): void {
    if (retryTimer) clearTimeout(retryTimer);
    retryTimer = null;
    retryAttempt = 0;
  }

  /**
   * The live session, restoring it from storage first if needed. A restore that could not be tried
   * (offline, server error) rejects rather than answering "signed out", so a route loader shows an
   * error instead of sending someone who is still signed in to the sign-in page.
   */
  async function ensureSession(): Promise<AnySession | null> {
    const state = store.getState();
    if (state.status === "idle") state.rehydrate();
    const { status, session } = store.getState();
    if (status === "authenticated" && session) return session;
    if (status === "anonymous") return null;
    await refreshSession();
    return store.getState().session;
  }

  function signOutLocally(options: { signedOut?: boolean } = {}): void {
    clearSession({ broadcast: true, signedOut: options.signedOut ?? false });
  }

  return { applySession, clearSession, refreshSession, ensureSession, signOutLocally };
}
